import { DocIdParams, DocMetaV1 } from '../dto/documents.js';
import { CatalogueParams, CatalogueQueryV1, CatalogueRowsV1, InboxCaptureRequestV1, InboxCountsV1, InboxDismissRequestV1, InboxPageV1, InboxQueryV1, LinkAllPreviewV1, LinkAllRequestV1, LinkAllResultV1, MergePreviewV1, MergeRequestV1, } from '../dto/hygiene.js';
import { InboxItemV1 } from '../inbox/categories.js';
import { defineRoute } from '../route/define-route.js';
import { EXAMPLE_IDS } from './example-ids.js';
import { EXAMPLE_CAPTURE_ITEM, EXAMPLE_CATALOGUE_ROWS, EXAMPLE_INBOX_COUNTS, EXAMPLE_INBOX_ITEM, EXAMPLE_LINK_ALL_PREVIEW, EXAMPLE_LINK_ALL_RESULT, EXAMPLE_MERGE_PREVIEW, EXAMPLE_MERGED_SURVIVOR, } from './examples-ledger.js';
/**
 * The catalogue (1.U), the Inbox and the hygiene actions (1.N-3) of exit 1b (1.0b deliverable
 * 19). Merge and Link all are canon only (A2.1 section 4): every document they name is an owner.
 */
const ids = EXAMPLE_IDS;
const exit1b = { auth: 'session', status: 'frozen', since: 'exit-1b' };
const hygiene = { ...exit1b, owner: '1.N-3' };
/** A type's documents as a table: a read, so a smoke session may call it. */
export const catalogueQuery = defineRoute({
    id: 'catalogue.query',
    method: 'POST',
    path: '/api/catalogue/:typeKey/query',
    params: CatalogueParams,
    body: CatalogueQueryV1,
    response: { kind: 'json', schema: CatalogueRowsV1 },
    smoke: 'read',
    reference: 'read',
    ...exit1b,
    owner: '1.U',
    examples: {
        request: {
            params: { typeKey: 'npc' },
            body: {
                filters: [{ column: 'loyalty', op: 'gte', value: 30 }],
                sort: [{ column: 'title', direction: 'asc' }],
                limit: 20,
            },
        },
        response: EXAMPLE_CATALOGUE_ROWS,
    },
});
/** The Inbox: every category, or one, paged; dismissed items and reference documents left out. */
export const inboxList = defineRoute({
    id: 'inbox.list',
    method: 'GET',
    path: '/api/inbox',
    query: InboxQueryV1,
    response: { kind: 'json', schema: InboxPageV1 },
    reference: 'none',
    ...hygiene,
    examples: {
        request: { query: { category: 'stub', limit: 50 } },
        response: { items: [EXAMPLE_INBOX_ITEM], next: null },
    },
});
export const inboxCounts = defineRoute({
    id: 'inbox.counts',
    method: 'GET',
    path: '/api/inbox/counts',
    response: { kind: 'json', schema: InboxCountsV1 },
    reference: 'none',
    ...hygiene,
    examples: { response: EXAMPLE_INBOX_COUNTS },
});
/** Ignore an item, this once or for good; a second dismissal of the same item replaces it. */
export const inboxDismiss = defineRoute({
    id: 'inbox.dismiss',
    method: 'POST',
    path: '/api/inbox/dismissals',
    body: InboxDismissRequestV1,
    response: { kind: 'empty' },
    reference: 'none',
    ...hygiene,
    examples: {
        request: {
            body: { category: 'stub', subjectId: ids.npcGuardCaptainRell.id, scope: 'once' },
        },
        response: null,
    },
});
/** Capture a note to the Inbox (Mod-Shift-K, decision 31), stored as a `capture`. */
export const inboxCapture = defineRoute({
    id: 'inbox.capture',
    method: 'POST',
    path: '/api/inbox/captures',
    body: InboxCaptureRequestV1,
    response: { kind: 'json', schema: InboxItemV1, status: 201 },
    reference: 'none',
    ...hygiene,
    examples: {
        request: { body: { text: 'Ask Orla about the salt barrels' } },
        response: EXAMPLE_CAPTURE_ITEM,
    },
});
/**
 * Merge errors: a merge into itself or into a merged document (422 `merge_invalid`), and a stale
 * `baseMetaVersion` (409 `version_conflict`, `conflictRevNo: null`, as on `docs.patchMeta`).
 */
const mergeErrors = { 409: ['version_conflict'], 422: ['merge_invalid'] };
const mergeRequest = {
    params: { id: ids.npcDockhand39.id },
    body: { survivorId: ids.npcDockhand40.id, baseMetaVersion: 1 },
};
export const hygieneMergePreview = defineRoute({
    id: 'hygiene.mergePreview',
    method: 'POST',
    path: '/api/docs/:id/merge/preview',
    params: DocIdParams,
    body: MergeRequestV1,
    response: { kind: 'json', schema: MergePreviewV1 },
    errors: mergeErrors,
    reference: 'deny',
    docIds: { owner: ['params.id', 'body.survivorId'] },
    ...hygiene,
    examples: { request: mergeRequest, response: EXAMPLE_MERGE_PREVIEW },
});
/** Merge the document of the path into the survivor; answers the survivor. */
export const hygieneMerge = defineRoute({
    id: 'hygiene.merge',
    method: 'POST',
    path: '/api/docs/:id/merge',
    params: DocIdParams,
    body: MergeRequestV1,
    response: { kind: 'json', schema: DocMetaV1 },
    errors: mergeErrors,
    reference: 'deny',
    docIds: { owner: ['params.id', 'body.survivorId'] },
    ...hygiene,
    examples: { request: mergeRequest, response: EXAMPLE_MERGED_SURVIVOR, consumes: true },
});
/** Every unlinked occurrence of the page's names that Link all would link. */
export const hygieneLinkAllPreview = defineRoute({
    id: 'hygiene.linkAllPreview',
    method: 'POST',
    path: '/api/docs/:id/unlinked/link-all/preview',
    params: DocIdParams,
    response: { kind: 'json', schema: LinkAllPreviewV1 },
    reference: 'deny',
    docIds: { owner: ['params.id'] },
    ...hygiene,
    examples: {
        request: { params: { id: ids.npcOldSef.id } },
        response: EXAMPLE_LINK_ALL_PREVIEW,
    },
});
/** Link all (decision 31): each occurrence as `mentions.link` names one; stale ones are skipped. */
export const hygieneLinkAll = defineRoute({
    id: 'hygiene.linkAll',
    method: 'POST',
    path: '/api/docs/:id/unlinked/link-all',
    params: DocIdParams,
    body: LinkAllRequestV1,
    response: { kind: 'json', schema: LinkAllResultV1 },
    reference: 'deny',
    docIds: { owner: ['params.id', 'body.occurrences[].sourceDocId'] },
    ...hygiene,
    examples: {
        request: {
            params: { id: ids.npcOldSef.id },
            body: {
                occurrences: [
                    {
                        sourceDocId: ids.questMissingLamplighters.id,
                        blockId: ids.blockMissingLamplightersOldSef.id,
                        occurrence: {
                            ordinal: 0,
                            baseBodyVersion: ids.questMissingLamplighters.state.bodyVersion,
                        },
                    },
                ],
            },
        },
        response: EXAMPLE_LINK_ALL_RESULT,
        consumes: true,
    },
});
//# sourceMappingURL=hygiene.js.map